import React, { FC, useMemo, useState, useRef, useCallback } from 'react';

import {
  Flex,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  IconButton,
} from '@chakra-ui/react';

import {
  CardAmexIcon,
  CardDiscoverIcon,
  CardMastercardIcon,
  CardVisaIcon,
  BankAccountIcon,
  CreditCardIcon,
} from '../../../../Icons';
import { MoreHorizontalIcon } from '../../../../Icons/MoreHorizontalIcon';
import { EColor } from '../../../../Theme';
import { useModalStore } from '../../../stores';
import { EModalContent } from '../../../types';
import { ECardBrand, IPaymentMethodNew } from '../types';
import { getIsExpiredCard } from '../utils';

interface IPaymentMethodProps {
  item: IPaymentMethodNew;
  isDefault: boolean;
  setDefaultPaymentMethod: (paymentMethod: IPaymentMethodNew) => Promise<void>;
}

const getPaymentMethodConfig = (method: IPaymentMethodNew) => {
  const iconProps = { h: '24px', maxH: '24px', w: 'auto' };

  const cardIconMap = {
    [ECardBrand.VISA]: <CardVisaIcon {...iconProps} />,
    [ECardBrand.MASTERCARD]: <CardMastercardIcon {...iconProps} />,
    [ECardBrand.AMEX]: <CardAmexIcon {...iconProps} />,
    [ECardBrand.DISCOVER]: <CardDiscoverIcon {...iconProps} />,
    [ECardBrand.UNKNOWN]: <CreditCardIcon {...iconProps} />,
  };

  if (method?.card) {
    return {
      icon: (method.card?.brand &&
        cardIconMap[method.card.brand as ECardBrand]) ?? (
        <CreditCardIcon {...iconProps} />
      ),
      name: '',
      last4: method.card?.lastFour,
    };
  }

  return {
    icon: (
      <BankAccountIcon
        {...iconProps}
        className="border-1 rounded-[3.5px] border-neutral-15"
      />
    ),
    name: method?.bank?.name,
    last4: method?.bank?.lastFour,
  };
};

const getCardDisplayName = (
  item: IPaymentMethodNew,
  paymentMethodConfig: ReturnType<typeof getPaymentMethodConfig>
) => {
  const lastFour = `•••• ${paymentMethodConfig?.last4 || ''}`;

  if (item?.card) {
    const { brand } = item.card;
    const brandNames: Record<string, string> = {
      [ECardBrand.VISA]: 'Visa',
      [ECardBrand.MASTERCARD]: 'Mastercard',
      [ECardBrand.AMEX]: 'Amex',
      [ECardBrand.DISCOVER]: 'Discover',
    };
    const brandName = brandNames[brand] || 'Card';
    return { brandName, lastFour };
  }
  if (item?.bank) {
    const bankName = paymentMethodConfig?.name || 'Bank';
    return { brandName: bankName, lastFour };
  }
  return { brandName: '', lastFour };
};

export const PaymentMethod: FC<IPaymentMethodProps> = ({
  item,
  isDefault,
  setDefaultPaymentMethod,
}) => {
  const paymentMethodConfig = getPaymentMethodConfig(item);
  const modalStore = useModalStore();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const lastOpenValueRef = useRef<boolean | null>(null);
  const justOpenedRef = useRef(false);

  const handleOpenChange = useCallback((open: boolean) => {
    if (lastOpenValueRef.current === open) return;
    lastOpenValueRef.current = open;

    if (open) {
      justOpenedRef.current = true;
      setTimeout(() => {
        justOpenedRef.current = false;
      }, 100);
    }

    setDropdownOpen(open);
  }, []);

  const isExpired = useMemo(() => {
    if (item?.card) {
      const expYear = item.card.expirationYear;
      const expMonth = item.card.expirationMonth;

      return getIsExpiredCard(expMonth, expYear);
    }

    return false;
  }, [item?.card]);

  const handleDeleteMethod = () => {
    modalStore.navigate({
      name: EModalContent.DELETE_METHOD_CONFIRMATION,
      paymentMethodId: item?.id!,
    });
  };

  const handleSetDefault = async () => {
    if (!isDefault && !isExpired) {
      await setDefaultPaymentMethod(item);
    }
  };

  const { brandName, lastFour } = getCardDisplayName(item, paymentMethodConfig);

  return (
    <Flex
      alignItems="center"
      bg={EColor.White}
      borderRadius="8px"
      gap="12px"
      p="8px"
      px="0"
      w="100%"
    >
      <Flex
        alignItems="center"
        bg={EColor.Neutral10}
        borderRadius="6px"
        h="32px"
        justifyContent="center"
        shrink="0"
        w="48px"
      >
        <Box
          alignItems="center"
          display="flex"
          h="24px"
          justifyContent="center"
          maxH="24px"
          maxW="48px"
          overflow="hidden"
        >
          {paymentMethodConfig && paymentMethodConfig.icon}
        </Box>
      </Flex>
      <Text
        color={EColor.Black}
        flex="1"
        fontSize="14px"
        fontWeight="400"
        lineHeight="18px"
        minW="0"
        textAlign="left"
      >
        {brandName && <Text as="span">{brandName} </Text>}
        <Text as="span" whiteSpace="nowrap">
          {lastFour}
        </Text>
      </Text>
      {isExpired && (
        <Flex
          alignItems="center"
          bg={EColor.Tomato}
          borderRadius="4px"
          gap="4px"
          px="8px"
          py="4px"
          shrink="0"
        >
          <Text
            color={EColor.Black}
            fontSize="12px"
            fontWeight="700"
            lineHeight="16px"
          >
            Expired
          </Text>
        </Flex>
      )}
      {isDefault && !isExpired && (
        <Flex
          alignItems="center"
          backdropFilter="blur(4px)"
          bg="blackAlpha.100"
          borderRadius="4px"
          gap="4px"
          px="8px"
          py="4px"
          shrink="0"
        >
          <Text
            color={EColor.Black}
            fontSize="12px"
            fontWeight="700"
            lineHeight="16px"
          >
            Default
          </Text>
        </Flex>
      )}
      {(!isDefault || isExpired) && (
        <Menu
          isOpen={dropdownOpen}
          onClose={() => handleOpenChange(false)}
          onOpen={() => handleOpenChange(true)}
        >
          <MenuButton
            aria-label="More options"
            as={IconButton}
            bg="transparent"
            border="none"
            icon={<MoreHorizontalIcon boxSize="16px" color={EColor.Black} />}
            p="4px"
            ref={triggerButtonRef}
            variant="ghost"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
          />
          <MenuList
            bg={EColor.White}
            border={`1px solid ${EColor.Neutral15}`}
            borderRadius="16px"
            boxShadow="0px 8px 36px 0px rgba(0,0,0,0.17)"
            minW="160px"
            p="8px"
            zIndex={20000}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
            }}
            onMouseDown={(e: React.MouseEvent) => {
              if (justOpenedRef.current) {
                e.preventDefault();
              }
            }}
          >
            <MenuItem
              _disabled={{
                cursor: 'not-allowed',
                opacity: 0.5,
                _hover: { bg: 'transparent' },
              }}
              _focus={{ bg: 'blackAlpha.50' }}
              _hover={{ bg: 'blackAlpha.50' }}
              bg="transparent"
              borderRadius="8px"
              color={EColor.Black}
              cursor="pointer"
              fontSize="14px"
              isDisabled={isExpired}
              lineHeight="20px"
              px="8px"
              py="8px"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleSetDefault();
                handleOpenChange(false);
              }}
            >
              Make default
            </MenuItem>
            <Box bg="blackAlpha.100" h="1px" mx="8px" my="0" />
            <MenuItem
              _focus={{ bg: 'blackAlpha.50' }}
              _hover={{ bg: 'blackAlpha.50' }}
              bg="transparent"
              borderRadius="8px"
              color="#DC2626"
              cursor="pointer"
              fontSize="14px"
              lineHeight="20px"
              px="8px"
              py="8px"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleDeleteMethod();
                handleOpenChange(false);
              }}
            >
              Delete
            </MenuItem>
          </MenuList>
        </Menu>
      )}
    </Flex>
  );
};
